import React from "react";
import type {
  SequenceStep,
  SendTimeOption,
  TimeUnit,
} from "../types";
import { DEFAULT_SEND_TIME_VALUE } from "../types";

interface SequenceEditorProps {
  sequence: SequenceStep[];
  sendTimeOptions: SendTimeOption[];
  onChange: (steps: SequenceStep[]) => void;
  onSave?: () => void;
  isSaving?: boolean;
}

const TIME_UNIT_LABELS: Record<TimeUnit, string> = {
  minutes: "Minutes",
  hours: "Hours",
  days: "Days",
  weeks: "Weeks",
  months: "Months",
};

const generateStepId = () =>
  `step-${Math.random().toString(36).slice(2, 10)}-${Date.now()}`;

const UNIT_IN_HOURS: Record<TimeUnit, number> = {
  minutes: 1 / 60,
  hours: 1,
  days: 24,
  weeks: 24 * 7,
  months: 24 * 30,
};

const toComparableHours = (step: SequenceStep) =>
  step.offsetValue * (UNIT_IN_HOURS[step.offsetUnit] ?? 1);

export const SequenceEditor: React.FC<SequenceEditorProps> = ({
  sequence,
  sendTimeOptions,
  onChange,
  onSave,
  isSaving = false,
}) => {
  const sortSteps = (steps: SequenceStep[]) => {
    return [...steps]
      .map((step, index) => ({ step, index }))
      .sort((a, b) => {
        const diff = toComparableHours(a.step) - toComparableHours(b.step);
        return diff === 0 ? a.index - b.index : diff;
      })
      .map(({ step }) => step);
  };

  const persist = (steps: SequenceStep[]) => {
    onChange(sortSteps(steps));
  };

  const updateStep = (
    stepId: string,
    updater: (step: SequenceStep) => SequenceStep
  ) => {
    const nextSteps = sequence.map((step) =>
      step.id === stepId ? updater(step) : step
    );
    persist(nextSteps);
  };

  const removeStep = (stepId: string) => {
    persist(sequence.filter((step) => step.id !== stepId));
  };

  const defaultSendTime =
    sendTimeOptions[0]?.value ?? DEFAULT_SEND_TIME_VALUE;

  const addStep = () => {
    const stepNumber = sequence.length + 1;
    persist([
      ...sequence,
      {
        id: generateStepId(),
        label: `Step ${stepNumber}`,
        offsetValue: 1,
        offsetUnit: "days",
        sendTimeOptionId: defaultSendTime,
        prompt:
          "Draft a personalized follow-up from {{characterName}} that references the last interaction and guides the user back into the story.",
      },
    ]);
  };

  const hasSteps = sequence.length > 0;

  return (
    <div className="space-y-4">
      {hasSteps ? (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          {sequence.map((step, index) => (
            <div
              key={step.id}
              className={`border border-gray-200 rounded-2xl p-5 bg-gray-50 w-full max-w-3xl ${
                index % 2 === 0 ? "justify-self-start" : "justify-self-end"
              }`}
          >
            <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
              <div>
                <div className="text-sm font-semibold text-gray-900">
                  {`Sequence Step ${index + 1}`}
                </div>
                <div className="mt-1 text-xs text-gray-500">
                  Trigger {step.offsetValue} {TIME_UNIT_LABELS[step.offsetUnit]}{" "}
                  after previous message.
                </div>
              </div>
              {sequence.length > 1 ? (
                <button
                  type="button"
                  className="text-xs font-semibold text-red-500 hover:text-red-600 bg-red-50 hover:bg-red-100 px-3 py-1.5 rounded-full transition-colors"
                  onClick={() => removeStep(step.id)}
                >
                  Remove
                </button>
              ) : null}
            </div>

            <div className="mt-4 sm:max-w-xl">
              <label className="flex flex-col gap-2">
                <span className="text-xs font-medium text-gray-600 uppercase tracking-wide">
                  Step Name
                </span>
                <input
                  type="text"
                  value={step.label}
                  onChange={(event) =>
                    updateStep(step.id, (current) => ({
                      ...current,
                      label: event.target.value,
                    }))
                  }
                  className="w-full rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)]"
                  placeholder="Weekly follow up"
                />
              </label>
            </div>

            <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-4 sm:max-w-5xl">
              <label className="flex flex-col gap-2">
                <span className="text-xs font-medium text-gray-600 uppercase tracking-wide">
                  Delay
                </span>
                <input
                  type="number"
                  min={0}
                  value={step.offsetValue}
                  onChange={(event) =>
                    updateStep(step.id, (current) => ({
                      ...current,
                      offsetValue: Number(event.target.value),
                    }))
                  }
                  className="w-full rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)]"
                />
              </label>
              <label className="flex flex-col gap-2">
                <span className="text-xs font-medium text-gray-600 uppercase tracking-wide">
                  Unit
                </span>
                <select
                  value={step.offsetUnit}
                  onChange={(event) =>
                    updateStep(step.id, (current) => ({
                      ...current,
                      offsetUnit: event.target.value as TimeUnit,
                    }))
                  }
                  className="w-full rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)]"
                >
                  {Object.entries(TIME_UNIT_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="flex flex-col gap-2 sm:col-span-2 sm:max-w-xl">
                <span className="text-xs font-medium text-gray-600 uppercase tracking-wide">
                  Time to send
                </span>
                <select
                  value={step.sendTimeOptionId ?? DEFAULT_SEND_TIME_VALUE}
                  onChange={(event) =>
                    updateStep(step.id, (current) => ({
                      ...current,
                      sendTimeOptionId: event.target.value,
                    }))
                  }
                  className="w-full rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)]"
                >
                  <option value={DEFAULT_SEND_TIME_VALUE}>
                    Use default timing
                  </option>
                  {sendTimeOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <label className="mt-4 flex flex-col gap-2">
              <span className="text-xs font-medium text-gray-600 uppercase tracking-wide">
                Prompt instructions
              </span>
              <textarea
                value={step.prompt}
                onChange={(event) =>
                  updateStep(step.id, (current) => ({
                    ...current,
                    prompt: event.target.value,
                  }))
                }
                rows={5}
                className="rounded-2xl border border-gray-200 bg-white px-4 py-3 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)]"
              />
            </label>
          </div>
          ))}
        </div>
      ) : (
        <div className="rounded-2xl border border-dashed border-gray-200 bg-white px-4 py-6 text-sm text-gray-500">
          No reengagement steps yet. Add one to define when the sequence should
          start emailing inactive users.
        </div>
      )}

      <div className="flex flex-wrap gap-3">
        <button
          type="button"
          onClick={addStep}
          className="inline-flex items-center justify-center rounded-xl border border-dashed border-gray-300 px-4 py-2.5 text-sm font-semibold text-[var(--primary-color)] hover:border-[var(--primary-color)] hover:bg-[var(--primary-color)]/10 transition-colors"
        >
          Add Sequence Step
        </button>
        {onSave ? (
          <button
            type="button"
            onClick={onSave}
            disabled={isSaving}
            className="inline-flex items-center justify-center rounded-xl bg-[var(--primary-color)] px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-60"
          >
            {isSaving ? "Saving..." : "Save sequence"}
          </button>
        ) : null}
      </div>
    </div>
  );
};
